import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * The one resize handle (`.claude/features/pane-divider.md`; Gerry,
 * 2026-10-02: "a wider big line", first purple, then "light blue actually.
 * Also more rectangular"): a 10px hit zone straddling a seam,
 * nothing painted at rest, a full-height 6px line in the divider blue
 * when hovered or focused, 7px and solid while dragging. Pointer capture
 * keeps the drag when the pointer leaves the handle; the body holds the
 * `col-resize` cursor and gives up text selection for the drag's life.
 *
 * The host keeps the value, the clamp and the storage: the divider reports
 * movement as `dx` from the press with the value at the press, and the
 * keyboard as a delta in the same units (`←`/`→` a step, ×4 with Shift,
 * `Home`/`End` to the limits). Double-click is the host's reset; a press
 * that never moves is `onPress` (the sidebar's edge toggles its pin).
 *
 * `placement`: `between` is a flex item of its own in a row (9px wide, the
 * two percent-split dividers' geometry), `left` / `right` lies over that
 * edge of its positioned parent. `passive` draws the line only — for a
 * host whose library owns the drag (the document overlay's re-resizable).
 */
export type PaneDividerProps = {
  label: string;
  placement: "between" | "left" | "right";
  value: number;
  min: number;
  max: number;
  /** Pointer movement since the press, in px, with the value at the press. */
  onDrag?: (dx: number, startValue: number) => void;
  onDragEnd?: (dx: number, startValue: number) => void;
  onDraggingChange?: (dragging: boolean) => void;
  /** A keyboard move, as a delta in the value's units (defaults to px). */
  onStep?: (delta: number) => void;
  /** The keyboard's unit; 16 unless the host's value is not pixels. */
  step?: number;
  /** `Home` / `End`: the limit itself. A host whose value grows leftward
   * (the workflow rail, the Files card) reads a step as seam movement, so
   * the limits cannot travel as steps; without this they are sent through
   * `onStep` / `onDrag` as a delta from the value. */
  onSet?: (value: number) => void;
  onReset?: () => void;
  /** A click that never became a drag. */
  onPress?: () => void;
  /** Extra grab room beyond the 10px zone, over a gutter the host owns. */
  grab?: { before?: number; after?: number };
  /** Shifts the whole handle sideways (px, positive to the right): a panel
   * that keeps a gutter between itself and its neighbour centres the line
   * in that gutter rather than on its own edge (Gerry, Oct 2: "doesn't
   * seem centered between all the panels"). */
  offset?: number;
  passive?: boolean;
  /** Folded away (two panes covering the seam): no pointer, no focus. */
  hidden?: boolean;
  /** A pointer affordance only, hidden from assistive tech whose path is
   * another control (the sidebar's toggle). */
  pointerOnly?: boolean;
  className?: string;
  testId?: string;
};

/** How far a press moves before it is a drag rather than a click. */
const PRESS_DRAG_PX = 4;
const STEP_PX = 16;
const STEP_BIG = 4;

const PaneDivider: React.FC<PaneDividerProps> = ({
  label,
  placement,
  value,
  min,
  max,
  onDrag,
  onDragEnd,
  onDraggingChange,
  onStep,
  step = STEP_PX,
  onSet,
  onReset,
  onPress,
  grab,
  offset = 0,
  passive = false,
  hidden = false,
  pointerOnly = false,
  className = "",
  testId,
}) => {
  const [dragging, setDragging] = useState(false);
  const pressRef = useRef<{
    pointerId: number;
    startX: number;
    startValue: number;
    moved: boolean;
  } | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const releaseBody = () => {
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
  };
  // An unmount mid-drag (navigating away with the button held) still
  // gives the body its cursor and selection back.
  useEffect(() => () => releaseBody(), []);

  const setDraggingBoth = useCallback(
    (next: boolean) => {
      setDragging(next);
      onDraggingChange?.(next);
    },
    [onDraggingChange]
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (passive || e.button !== 0) return;
    e.preventDefault();
    pressRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startValue: valueRef.current,
      moved: false,
    };
    // Captured by the grab zone, the one part of the handle that takes the
    // pointer (see below); its events bubble here.
    try {
      (e.target as Element).setPointerCapture(e.pointerId);
    } catch {
      // A synthetic pointer has no capture to take.
    }
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    const dx = e.clientX - press.startX;
    if (!press.moved) {
      if (Math.abs(dx) < PRESS_DRAG_PX) return;
      press.moved = true;
      setDraggingBoth(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    onDrag?.(dx, press.startValue);
  };
  const finish = (e: React.PointerEvent<HTMLDivElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    pressRef.current = null;
    try {
      (e.target as Element).releasePointerCapture(e.pointerId);
    } catch {
      // Already released.
    }
    if (press.moved) {
      setDraggingBoth(false);
      releaseBody();
      onDragEnd?.(e.clientX - press.startX, press.startValue);
    } else if (e.type === "pointerup") {
      // A press that never moved. A double-click is two presses and then
      // the reset: a host whose press toggles (the sidebar) toggles twice,
      // which is where it started, and then resets its width.
      onPress?.();
    }
  };
  const stepBy = (delta: number) => {
    if (onStep) onStep(delta);
    else {
      onDrag?.(delta, valueRef.current);
      onDragEnd?.(delta, valueRef.current);
    }
  };
  const setTo = (target: number) => {
    if (onSet) onSet(target);
    else stepBy(target - valueRef.current);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (passive) return;
    const unit = step * (e.shiftKey ? STEP_BIG : 1);
    if (e.key === "ArrowRight") stepBy(unit);
    else if (e.key === "ArrowLeft") stepBy(-unit);
    else if (e.key === "Home") setTo(min);
    else if (e.key === "End") setTo(max);
    else return;
    e.preventDefault();
  };

  // The item's own box: between two columns it is a 9px flex item (the
  // percent dividers' geometry, which their hosts' shares account for);
  // on an edge it lies over the seam without taking layout.
  const box =
    placement === "between"
      ? "relative mx-[2px] w-[5px] flex-none self-stretch"
      : placement === "left"
        ? "absolute inset-y-0 -left-[5px] w-[10px]"
        : "absolute inset-y-0 -right-[5px] w-[10px]";
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      aria-hidden={hidden || pointerOnly || undefined}
      tabIndex={passive || hidden || pointerOnly ? -1 : 0}
      data-pane-divider=""
      data-dragging={dragging || undefined}
      data-testid={testId}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onDoubleClick={passive ? undefined : onReset}
      onKeyDown={onKeyDown}
      // The box itself never takes the pointer: only the grab zone does, so
      // a negative `grab` really narrows what the handle covers (the Files
      // panel's zone stays inside its border, off the transcript's
      // scrollbar). Its events bubble here, and `:hover` reaches the box.
      className={`group/divider pointer-events-none z-30 outline-none max-md:hidden ${
        passive ? "" : "cursor-col-resize"
      } ${box} ${className}`}
      style={offset ? { transform: `translateX(${offset}px)` } : undefined}
    >
      {/* The grab zone: the 10px round the seam, plus whatever gutter the
          host lends it, laid over without taking layout. */}
      <span
        aria-hidden
        data-divider-grab=""
        className={`absolute inset-y-0 ${hidden ? "" : "pointer-events-auto"}`}
        style={{
          left: -(placement === "between" ? 2.5 : 0) - (grab?.before ?? 0),
          right: -(placement === "between" ? 2.5 : 0) - (grab?.after ?? 0),
        }}
      />
      {/* The line: the whole height, centred on the seam. */}
      <span
        aria-hidden
        data-divider-line=""
        className={`pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 rounded-[1px] transition-[opacity,width,background-color] duration-150 ${
          dragging
            ? "w-[7px] bg-[var(--fp-divider)] opacity-100"
            : "w-[6px] bg-[var(--fp-divider-soft)] opacity-0 group-hover/divider:opacity-100 group-focus-visible/divider:opacity-100"
        }`}
      />
    </div>
  );
};

export default PaneDivider;
